import moment from "moment/min/moment-with-locales";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Chart } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState } from "react";
import ErrorResponse from "../ErrorResponse";
import { Card, Col } from "react-bootstrap";
import LoadingIndicator from "../LoadingIndicator";
import { mapItemType, mapItemTypeColor } from "../HelperFunctions";
import { useRouteLoaderData } from "react-router-dom";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

export default function ContentBalanceTrendChart({ data, averages = null, loading, error, grouping, averageLabel = "AVG" }) {
    const { radios, competitorRadios } = useRouteLoaderData("root");
    const [resolvedRadios, setResolvedRadios] = useState([]);

    useEffect(() => {
        async function resolveRadios() {
            setResolvedRadios((await radios).data.concat((await competitorRadios).data));
        }
        if (radios && competitorRadios) resolveRadios();
    }, [radios, competitorRadios]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        borderRadius: 5,
        pointStyle: "rectRounded",
        plugins: {
            legend: {
                labels: {
                    usePointStyle: true,
                    filter: function (legendItem, data) {
                        const dataset = data.datasets[legendItem.datasetIndex];

                        if (dataset.type === "line") {
                            return true;
                        }

                        if (dataset.stack === "Stack 0") {
                            const labelParts = legendItem.text.split(" ");
                            legendItem.text = labelParts[labelParts.length - 1];
                            return true;
                        }
                        return false;
                    },
                },
            },
            datalabels: {
                color: "white",
                font: {
                    weight: "bold",
                },
                formatter: function (value, context) {
                    if (context.dataset.type === "line") return "";
                    return value > 0 ? `${Math.round(value)}%` : "";
                },
            },
            tooltip: {
                callbacks: {
                    label: function (item) {
                        return item.raw ? `${item.dataset.label}: ${item.raw.toFixed(0)}%` : "";
                    },
                },
            },
        },
        scales: {
            y: {
                ticks: {
                    display: false,
                },
                stacked: true,
                beginAtZero: true,
                max: 100,
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
            },
            x: {
                stacked: true,
                ticks: {
                    align: "center",
                },
                grid: {
                    display: false,
                },
            },
        },
        interaction: {
            mode: "index",
        },
    };
    const chartData = useMemo(() => {
        if (data && data.length > 0) {
            const datasets = [];
            const labels = data.map(x => {
                const date = moment(x.date);

                if (grouping === 1 || grouping === 2) {
                    return [date.format("LT"), date.format("DD/MM")];
                }

                return date.format(grouping > 2 ? "L" : "LT");
            });

            const getAverageValue = balance => {
                if (!balance) return null;

                const averageKeys = ["average", "avg", "avg_value", "average_value", "last_year_average"];
                const averageKey = averageKeys.find(key => balance[key] !== undefined && balance[key] !== null);

                if (!averageKey) return null;

                const averageValue = Number(balance[averageKey]);
                return Number.isFinite(averageValue) ? averageValue : null;
            };

            for (let ri = 0; ri < data[0].radios.length; ++ri) {
                const currentRadio = data[0].radios[ri];

                for (let iti = 0; iti < currentRadio.balances.length; ++iti) {
                    const balance = currentRadio.balances[iti];
                    const radioName = resolvedRadios.find(x => x.radio_id === currentRadio.radio_id)?.radio_name ?? "Station";
                    const itemType = mapItemType(balance.item_type);
                    const itemColor = mapItemTypeColor(balance.item_type);
                    const averageData = data.map((x, pointIndex) => {
                        const averagePoint = averages?.[pointIndex] ?? averages?.find(avg => avg.date === x.date);
                        const foundBalance =
                            averagePoint?.radios[ri]?.balances.find(b => b.item_type === balance.item_type) ??
                            x.radios[ri]?.balances.find(b => b.item_type === balance.item_type);
                        return getAverageValue(foundBalance);
                    });

                    datasets.push({
                        type: "bar",
                        label: `${radioName} ${itemType}`,
                        data: data.map(x => {
                            const foundBalance = x.radios[ri]?.balances.find(b => b.item_type === balance.item_type);
                            return foundBalance ? foundBalance.value : 0;
                        }),
                        backgroundColor: data.map(x => itemColor),
                        stack: "Stack " + ri,
                    });

                    if (averageData.some(value => value !== null)) {
                        datasets.push({
                            type: "line",
                            label: `${radioName} ${itemType} ${averageLabel}`,
                            data: averageData,
                            borderColor: itemColor,
                            backgroundColor: itemColor,
                            borderDash: [6, 4],
                            borderWidth: 2,
                            pointRadius: grouping > 2 ? 2 : 0,
                            pointHoverRadius: 4,
                            tension: 0.25,
                            spanGaps: true,
                            order: 0,
                        });
                    }
                }
            }
            return {
                labels: labels,
                datasets: datasets,
            };
        }
        return null;
    }, [averageLabel, averages, data, resolvedRadios, grouping]);

    return (
        <Card>
            <Card.Header className="text-center">Content Balance Trend</Card.Header>
            <Card.Body className="d-flex flex-column p-1 justify-content-center overflow-auto">
                {loading ? (
                    <LoadingIndicator width={75} />
                ) : error ? (
                    <ErrorResponse errorCode={error} />
                ) : (
                    data && (
                        <Col className="dashboard-chart content-balance">
                            <Chart data={chartData} options={chartOptions} />
                        </Col>
                    )
                )}
            </Card.Body>
        </Card>
    );
}
